import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import {
  CaretDownIcon,
  MagnifyingGlassIcon,
  XIcon,
  PlusCircleIcon
} from "@phosphor-icons/react";

import { CommonDashboardWrapper } from "styles/GlobalStyles";
import {
  CardFlexWrapperStyles,
  CardWrapperStyles,
  MarketplaceWrapperStyle
} from "../styled";
import { DotOutlineFillIcon } from "assets/SVGComponents";
import SkeletonLoader from "components/PageLoader/SkeletonLoader";
import Button from "components/Buttons";
import Icon from "components/Icon/Icon";
import { selectCampaignReport } from "../slice/selector";
import { useMarketplaceSlice } from "../slice";
import CommonTable from "components/Tables/CommonTable";
import columns from "./column";
import CampaignReportModal from "./CampaignReportModal";
import PillButtons from "components/Buttons/PillButtons";
import { TableWrapperStyles } from "pages/Creators/styled";

const CampaignReport = () => {
  const dispatch = useDispatch();
  const { actions } = useMarketplaceSlice();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("Pending");

  const {
    reports,
    status_counts,
    poster_counts,
    posterCountsLoading,
    pagination,
    loading,
    initialLoading
  } = useSelector(selectCampaignReport);

  const initialSortBy = searchParams.get("sort_by") || "id";
  const initialSortOrder = searchParams.get("sort_order") || "desc";

  useEffect(() => {
    dispatch(
      actions.fetchCampaignReports({
        page: Number(searchParams.get("page")) || 1,
        limit: 10,
        sort_by: searchParams.get("sort_by") || null,
        sort_order: searchParams.get("sort_order") || "desc",
        report_status: searchParams.get("report_status") || ""
      })
    );
  }, [searchParams]);

  const handlePageChange = (newPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", newPage);
      return next;
    });
  };

  const handleSortChange = ({ sort_by, sort_order }) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("sort_by", sort_by);
      next.set("sort_order", sort_order);
      next.set("page", 1);
      return next;
    });
  };

  const handleEdit = (row) => {
    // only reset if different row
    if (selectedRow?.id !== row?.id) {
      setSelectedRow({ ...row });
    }
    setShowEditModal(true);

    dispatch(
      actions.fetchPosterCounts({
        userId: row?.user_id
      })
    );
  };

  const handleCloseModal = () => {
    setSelectedRow(null);
    setShowEditModal(false);
  };

  const statusMenuItems = [
    { label: "All", value: "all", count: status_counts.total },
    { label: "Pending", value: "pending", count: status_counts.pending },
    { label: "Ignored", value: "ignored", count: status_counts.ignored },
    {
      label: "Hidden",
      value: "hidden",
      count: status_counts.hidden
    }
  ];

  const handleStatusSelect = (item) => {
    setSelectedStatus(item.label);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (item.value === "all") {
        next.delete("report_status"); // remove param entirely for "all"
      } else {
        next.set("report_status", item.value);
      }
      next.set("page", 1);
      return next;
    });
  };

  return (
    <>
      <MarketplaceWrapperStyle>
        <CommonDashboardWrapper>
          <div className="common-dashboard-wrapper">
            <h1 className="common-heading">
              Campaigns Reported as Issues by Creators
            </h1>
          </div>

          <CardFlexWrapperStyles>
            <CardWrapperStyles>
              <div className="card-list">
                <DotOutlineFillIcon color="#F5A000" />
                <span className="card-title">Pending Reports</span>
              </div>
              {initialLoading ? (
                <SkeletonLoader width="40px" />
              ) : (
                <p className="card-count">{status_counts.pending || "-"}</p>
              )}
            </CardWrapperStyles>

            <CardWrapperStyles>
              <div className="card-list">
                <DotOutlineFillIcon color="#D8001A" />
                <span className="card-title">Ignored Reports</span>
              </div>
              {initialLoading ? (
                <SkeletonLoader width="40px" />
              ) : (
                <p className="card-count">{status_counts.ignored || "-"}</p>
              )}
            </CardWrapperStyles>

            <CardWrapperStyles>
              <div className="card-list">
                <DotOutlineFillIcon color="#008491" />
                <span className="card-title">Hidden Reports</span>
              </div>
              {initialLoading ? (
                <SkeletonLoader width="40px" />
              ) : (
                <p className="card-count">{status_counts.hidden || "-"}</p>
              )}
            </CardWrapperStyles>
          </CardFlexWrapperStyles>

          <TableWrapperStyles>
            <div className="filter-bar">
              <PillButtons
                placeholderText="Status"
                mainLabelText={selectedStatus}
                rightIcon={<Icon component={CaretDownIcon} />}
                menuItems={statusMenuItems}
                onSelect={handleStatusSelect}
                size="M"
                maxWidth="144px"
              />
            </div>

            <CommonTable
              data={reports}
              columns={columns(handleEdit)}
              loading={loading}
              pagination={pagination}
              total_count={pagination?.total}
              page={Number(searchParams.get("page")) || 1}
              tableWrapperStyles="table-wrapper-styles"
              limit={pagination?.limit || 10}
              onPageChange={handlePageChange}
              sort_by={initialSortBy}
              sort_order={initialSortOrder}
              onSortChange={handleSortChange}
              initialLoading={initialLoading}
              tableClassName="creators-table"
            />
          </TableWrapperStyles>
        </CommonDashboardWrapper>
      </MarketplaceWrapperStyle>

      <CampaignReportModal
        show={showEditModal}
        onHide={handleCloseModal}
        rowData={selectedRow}
        status_counts={status_counts}
        poster_counts={poster_counts}
        posterCountsLoading={posterCountsLoading}
      />
    </>
  );
};

export default CampaignReport;
